/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { create } from 'zustand';

/*
 * The theme does not live here any more. It is held by the kit's `ThemeProvider` (mounted in
 * `app/providers.tsx`), which owns the `data-theme` attribute, the `color-scheme`, the OS-preference
 * listener, and persistence. Holding a copy in this store meant two things could disagree about
 * what was on screen, and only one of them was painting it.
 *
 * Read it with `useTheme()` from `@vaas/react-ui-kit`; write it with `useThemeToggle`, which is
 * still the single path that also persists to the user-settings API.
 */

/**
 * Quick-tour context. Each page with something worth showing has its own short tour; `app` covers
 * the shared chrome (navigation, vehicle, help, feedback) everywhere else.
 */
export type TourKind = 'home' | 'explorer' | 'analytics' | 'admin' | 'app';

/** Active vehicle identity shown in the top bar while the explorer is open. */
export interface VehicleIdentity {
  /** Description for print/header (may fall back to program code when no make/model). */
  name: string;
  vin?: string;
  /** Human vehicle details ("2025 Cadillac Lyriq"), without program code. Safe for all users. */
  details?: string;
  /** Program code, e.g. "C223". Internal-only for display. */
  program?: string;
  /** Engineering / build release, e.g. "APPV". Internal-only for display. */
  release?: string;
  /** Street trim of a launched VIN, e.g. "AT4". Display-only. */
  trim?: string;
}

interface UiState {
  /** Desktop: the rail is widened to name its destinations, moving the page over (persisted). */
  railPinned: boolean;
  setRailPinned: (pinned: boolean) => void;
  /** Tablet: the named rail is open over the page, until the next pick, click elsewhere or Escape. */
  railPeek: boolean;
  setRailPeek: (open: boolean) => void;

  /** Mobile/tablet: whether the off-canvas navigation rail drawer is open. Desktop ignores this. */
  navOpen: boolean;
  openNav: () => void;
  closeNav: () => void;
  toggleNav: () => void;

  vehicle: VehicleIdentity | null;
  setVehicle: (vehicle: VehicleIdentity | null) => void;

  /** Whether the top-bar vehicle picker dropdown is expanded (lets pages avoid hiding behind it). */
  pickerOpen: boolean;
  setPickerOpen: (open: boolean) => void;

  /** Last visited URL per tool so the nav rail restores URL-driven state. */
  routeMemory: Record<string, string>;
  rememberRoute: (basePath: string, url: string) => void;

  resourcesOpen: boolean;
  openResources: () => void;
  closeResources: () => void;

  feedbackOpen: boolean;
  openFeedback: () => void;
  closeFeedback: () => void;

  releaseNotesOpen: boolean;
  openReleaseNotes: () => void;
  closeReleaseNotes: () => void;

  aboutOpen: boolean;
  openAbout: () => void;
  closeAbout: () => void;

  /** Quick tour: open state and which page's tour is showing. */
  tourOpen: boolean;
  tourKind: TourKind;
  /**
   * Tours the user has taken, skipped, or declined the invitation to (persisted). A first visit to
   * a page offers its tour once; the Help menu can always start it again.
   */
  toursSeen: TourKind[];
  startTour: (kind: TourKind) => void;
  /** Closes the tour (finished or skipped) and remembers it as seen. */
  endTour: () => void;
  /** "Not now" on the first-visit invitation: remembered, so it is not offered again. */
  dismissTourInvite: (kind: TourKind) => void;

  /** Whether the blocking safety disclaimer is currently shown; owned by DisclaimerModal. */
  disclaimerBlocking: boolean;
  setDisclaimerBlocking: (blocking: boolean) => void;
}

const RAIL_PINNED_KEY = 'edx-rail-pinned';

const readRailPinned = (): boolean => {
  try {
    return localStorage.getItem(RAIL_PINNED_KEY) === '1';
  } catch {
    return false;
  }
};

const writeRailPinned = (pinned: boolean) => {
  try {
    localStorage.setItem(RAIL_PINNED_KEY, pinned ? '1' : '0');
  } catch {
    /* storage unavailable */
  }
};

const TOURS_SEEN_KEY = 'edx-tours-seen';
/** Pre-2026 flags: the whole-app tour and the explorer tour each had their own. */
const LEGACY_APP_TOUR_KEY = 'edx-tour-seen';
const LEGACY_EXPLORER_TOUR_KEY = 'edx-explorer-tour-seen';

const TOUR_KINDS: readonly TourKind[] = ['home', 'explorer', 'analytics', 'admin', 'app'];

/**
 * Tours already seen. Someone who took the old whole-app tour has seen Home and the chrome, and the
 * old explorer flag carries over, so nobody is offered a tour they have already been through.
 */
const readToursSeen = (): TourKind[] => {
  try {
    const seen = new Set<TourKind>();
    const stored: unknown = JSON.parse(localStorage.getItem(TOURS_SEEN_KEY) ?? '[]');
    if (Array.isArray(stored)) {
      for (const kind of TOUR_KINDS) if (stored.includes(kind)) seen.add(kind);
    }
    if (localStorage.getItem(LEGACY_APP_TOUR_KEY) === '1') seen.add('home').add('app');
    if (localStorage.getItem(LEGACY_EXPLORER_TOUR_KEY) === '1') seen.add('explorer');
    return [...seen];
  } catch {
    return [];
  }
};

const writeToursSeen = (seen: TourKind[]) => {
  try {
    localStorage.setItem(TOURS_SEEN_KEY, JSON.stringify(seen));
  } catch {
    /* storage unavailable */
  }
};

/** `seen` plus `kind`, persisted; the same array when it was already there. */
const withSeen = (seen: TourKind[], kind: TourKind): TourKind[] => {
  if (seen.includes(kind)) return seen;
  const next = [...seen, kind];
  writeToursSeen(next);
  return next;
};

/** Lightweight UI state; server data lives in TanStack Query. */
export const useUiStore = create<UiState>((set) => ({
  railPinned: readRailPinned(),
  setRailPinned: (railPinned) => {
    writeRailPinned(railPinned);
    set({ railPinned });
  },
  railPeek: false,
  setRailPeek: (railPeek) => set({ railPeek }),

  navOpen: false,
  openNav: () => set({ navOpen: true }),
  closeNav: () => set({ navOpen: false }),
  toggleNav: () => set((s) => ({ navOpen: !s.navOpen })),

  vehicle: null,
  setVehicle: (vehicle) => set({ vehicle }),

  pickerOpen: false,
  setPickerOpen: (pickerOpen) => set({ pickerOpen }),

  routeMemory: {},
  rememberRoute: (basePath, url) =>
    set((s) =>
      s.routeMemory[basePath] === url ? s : { routeMemory: { ...s.routeMemory, [basePath]: url } },
    ),

  resourcesOpen: false,
  openResources: () => set({ resourcesOpen: true }),
  closeResources: () => set({ resourcesOpen: false }),

  feedbackOpen: false,
  openFeedback: () => set({ feedbackOpen: true }),
  closeFeedback: () => set({ feedbackOpen: false }),

  releaseNotesOpen: false,
  openReleaseNotes: () => set({ releaseNotesOpen: true }),
  closeReleaseNotes: () => set({ releaseNotesOpen: false }),

  aboutOpen: false,
  openAbout: () => set({ aboutOpen: true }),
  closeAbout: () => set({ aboutOpen: false }),

  tourOpen: false,
  tourKind: 'app',
  toursSeen: readToursSeen(),
  startTour: (tourKind) => set({ tourOpen: true, tourKind }),
  endTour: () => set((s) => ({ tourOpen: false, toursSeen: withSeen(s.toursSeen, s.tourKind) })),
  dismissTourInvite: (kind) => set((s) => ({ toursSeen: withSeen(s.toursSeen, kind) })),

  // Default blocking until DisclaimerModal reports otherwise.
  disclaimerBlocking: true,
  setDisclaimerBlocking: (disclaimerBlocking) => set({ disclaimerBlocking }),
}));
